import { EVENT } from "@/constants/event";
import useAppStore from "@/context/store";
import { gdImgSrcPolicy } from "@/utils/imageUrl";
import classNames from "classnames";
import { shuffle } from "lodash";
import { useCallback, useMemo, useState } from "react";
import { Case, Switch } from "react-if";
import HBIcon from "../HBIcon";
import Image from "../Image";
import styles from "./index.module.less";

export default function QuestionaireCard({ record, distCodes, fileIds }) {
  const user = useAppStore(({ user }) => user);
  const [progress, setProgress] = useState(0);
  const [answer, setAnswer] = useState(null);

  const ImageIcons = useMemo(() => {
    // 随机展示图片
    return shuffle([
      "https://grocery-cdn.huaban.com/file/b475e7f089fa11ef911b1f4815c133e0.png",
      "https://grocery-cdn.huaban.com/file/a9904b5089fa11efb9fe5bcda5c85637.png",
      "https://grocery-cdn.huaban.com/file/a998d6d089fa11ef911b1f4815c133e0.png",
      "https://grocery-cdn.huaban.com/file/a998138089fa11ef911b1f4815c133e0.png",
      "https://grocery-cdn.huaban.com/file/a997774089fa11ef911b1f4815c133e0.png",
      "https://grocery-cdn.huaban.com/file/a99617b089fa11ef911b1f4815c133e0.png",
      "https://grocery-cdn.huaban.com/file/a9ab747089fa11efb9fe5bcda5c85637.png",
    ]);
  }, []);

  const onSelectAnswer = useCallback((answer) => {
    setProgress(1);
    setAnswer(answer);
  }, []);

  const onSelectOption = useCallback(() => {
    setProgress(2);
  }, []);

  const onSatisfy = useCallback(() => {
    setProgress(2);
  }, []);

  const onClose = useCallback(
    (evt) => {
      evt.preventDefault();
      const pinId = record.pin_id;
      // 判断是否是采集还是其他类型
      if (pinId) {
        window.dispatchEvent(
          new CustomEvent(EVENT.DELETE_PIN, { detail: pinId })
        );
      }
    },
    [record]
  );

  return (
    <div
      className={styles.card}
      data-gd-expose="questionnaire_expose"
      data-questionnaire-id={record.id}
      data-questionnaire-title="搜索结果满意度调查"
      data-questionnaire-type="用户反馈"
      data-create-id={record.id}
      data-create-title={record.name}
    >
      <div className={styles.container}>
        <HBIcon className={styles.close} type="ic_close" onClick={onClose} />
        <Switch>
          <Case condition={progress === 0}>
            <div className={styles.content}>
              <div className={styles.title}>内容质量反馈</div>
              <div className={styles.list}>
                <div
                  className={styles.item}
                  title="满意"
                  data-gd-click="questionnaire_click"
                  data-questionnaire-id={record.id}
                  data-questionnaire-title="你对当前结果满意吗？"
                  data-options-title="满意"
                  data-click-type="提交"
                  data-questionnaire-type="用户反馈"
                  data-primary-filter-title="满意"
                  data-file-ids={fileIds?.join(",") || ""}
                  data-distcodes={distCodes?.join(",") || ""}
                  data-create-id={record.id}
                  data-create-title={record.name}
                  data-satisfaction-degree="满意"
                  data-search-word={record.search_word || ""}
                  onClick={onSatisfy}
                >
                  <div className={styles.icon}>
                    <Image
                      {...gdImgSrcPolicy(
                        "https://grocery-cdn.huaban.com/file/288f731089f411ef911b1f4815c133e0.png",
                        {
                          width: 28,
                        }
                      )}
                      width={28}
                      height={28}
                      alt="满意"
                    />
                  </div>
                  <div className={styles.text}>满意</div>
                </div>
                {record?.answers?.map((answer, index) => (
                  <div
                    className={styles.item}
                    key={index}
                    onClick={() => onSelectAnswer(answer)}
                    title={answer?.title || ""}
                    data-gd-click="questionnaire_click"
                    data-questionnaire-id={record.id}
                    data-questionnaire-title="内容质量反馈"
                    data-options-title={answer?.title || ""}
                    data-click-type="提交"
                    data-questionnaire-type="用户反馈"
                    data-primary-filter-title="不满意"
                    data-second-filter-title={answer?.title || ""}
                    data-file-ids={fileIds?.join(",") || ""}
                    data-distcodes={distCodes?.join(",") || ""}
                    data-create-id={record.id}
                    data-create-title={record.name}
                    data-satisfaction-degree="不满意"
                    data-feedback-content={answer?.title || ""}
                    data-search-word={record.search_word || ""}
                  >
                    <div className={styles.icon}>
                      <Image
                        {...gdImgSrcPolicy(ImageIcons[index % 7], {
                          width: 28,
                        })}
                        width={28}
                        height={28}
                        alt="满意"
                      />
                    </div>
                    <div className={styles.text}>{answer?.title || ""}</div>
                  </div>
                ))}
              </div>
            </div>
          </Case>
          <Case condition={progress === 1}>
            <div className={styles.content}>
              <div
                className={classNames([styles.title, styles.row2])}
                title={answer?.question || ""}
              >
                {answer?.question || ""}
              </div>
              <div className={styles.list}>
                {answer?.options?.map((option, index) => (
                  <div
                    className={styles.item}
                    key={index}
                    title={option?.question}
                    data-gd-click="questionnaire_click"
                    data-questionnaire-id={record.id}
                    data-questionnaire-title={answer?.question || ""}
                    data-options-title={option?.value || ""}
                    data-click-type="提交"
                    data-questionnaire-type="用户反馈"
                    data-primary-filter-title="不满意"
                    data-second-filter-title={answer?.title || ""}
                    data-third-filter-title={option?.value || ""}
                    data-file-ids={fileIds?.join(",") || ""}
                    data-distcodes={distCodes?.join(",") || ""}
                    data-create-id={record.id}
                    data-create-title={record.name}
                    data-satisfaction-degree="不满意"
                    data-feedback-content={answer?.title || ""}
                    data-feedback-type={option?.value || ""}
                    data-search-word={record.search_word || ""}
                    onClick={onSelectOption}
                  >
                    <div className={styles.text}>{option?.value || ""}</div>
                  </div>
                ))}
              </div>
            </div>
          </Case>
          <Case condition={progress === 2}>
            <div className={styles.success}>
              <img
                src="https://grocery-cdn.huaban.com/file/f8f17ab089f611ef911b1f4815c133e0.png"
                alt="感谢你的反馈"
                height={98}
                width={98}
              />
              <div className={styles.title}>感谢你的反馈，我们会尽快优化</div>
              <form method="post" action="https://support.qq.com/product/30852">
                <input
                  type="hidden"
                  name="customInfo"
                  value={`用户ID：${user?.user_id}`}
                />
                <button className={styles.link} type="submit">
                  反馈更多问题和意见
                </button>
              </form>
            </div>
          </Case>
        </Switch>
      </div>
    </div>
  );
}
